import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Reviews from "../components/Reviews";
import { useAuth } from "../context/AuthContext";
import API_BASE_URL from "../config";
import {
  FaMapMarkerAlt,
  FaBriefcase,
  FaGraduationCap,
  FaCalendarAlt,
  FaCheckCircle,
  FaBalanceScale,
  FaGavel,
  FaVideo,
  FaBuilding,
  FaCertificate,
  FaClock,
  FaArrowRight,
  FaUserTie
} from "react-icons/fa";
import { FaAward } from "react-icons/fa";
import { FaShareAlt } from "react-icons/fa";
import { FaCrown, FaMedal } from "react-icons/fa";
import "../styles/SolicitorDetail.css";

export default function SolicitorDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [solicitor, setSolicitor] = useState(null);
  const [about, setAbout] = useState(null);
  const [loading, setLoading] = useState(true);

  // ✅ Booking selection state
  const [showBooking, setShowBooking] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedArea, setSelectedArea] = useState("");
  const bookingRef = useRef(null);
  const [showAllVideos, setShowAllVideos] = useState(false);


  /* ======================
     FETCH DATA
  ====================== */
  useEffect(() => {
    const fetchAll = async () => {
      try {
        const userRes = await fetch(`${API_BASE_URL}/api/advocates/${id}`);
        const userData = await userRes.json();

        const aboutRes = await fetch(
          `${API_BASE_URL}/api/solicitor/about/${userData.solicitorId}`
        );
        const aboutData = await aboutRes.json();

        setSolicitor(userData);
        setAbout(aboutData);
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, [id]);

  useEffect(() => {
    if (!id) return;

    let viewedList = JSON.parse(sessionStorage.getItem("viewedSolicitors")) || [];

    // Remove if already exists
    viewedList = viewedList.filter(sid => sid !== id);

    // Add to front
    viewedList.unshift(id);

    // Save back
    sessionStorage.setItem("viewedSolicitors", JSON.stringify(viewedList));
  }, [id]);


  /* ======================
     PRACTICE % (NO CONSTANTS)
  ====================== */
  const getPracticePercentage = (areas = []) => {
    if (areas.length >= 3) return 90;
    if (areas.length === 2) return 65;
    if (areas.length === 1) return 40;
    return 0;
  };

  const getBarColorClass = (percentage) => {
    if (percentage >= 75) return "advocate-bar-green";
    if (percentage >= 50) return "advocate-bar-orange";
    return "advocate-bar-red";
  };

  /* ======================
     VIDEO EMBED FIX
  ====================== */
  const getEmbedUrl = (url) => {
    if (!url) return "";
    if (url.includes("youtube.com/watch")) {
      const id = new URL(url).searchParams.get("v");
      return `https://www.youtube.com/embed/${id}`;
    }
    if (url.includes("youtu.be")) {
      return `https://www.youtube.com/embed/${url.split("/").pop()}`;
    }
    if (url.includes("vimeo.com")) {
      return `https://player.vimeo.com/video/${url.split("/").pop()}`;
    }
    return url;
  };

  /* ======================
     BOOK APPOINTMENT ACTION
  ====================== */
  const proceedBooking = () => {
    if (!selectedCategory || !selectedArea) {
      alert("Please select practice category and area");
      return;
    }

    const bookingPath = `/book-appointment/${solicitor._id}`;

    const bookingState = {
      practiceCategory: selectedCategory,
      practiceArea: selectedArea
    };

    // ✅ If user NOT authenticated → go to login
    if (!isAuthenticated || !user) {
      navigate("/login", {
        state: {
          redirectTo: bookingPath,
          bookingState: bookingState
        }
      });
      return;
    }

    // ✅ If authenticated → go directly
    navigate(bookingPath, {
      state: bookingState
    });
  };


  /* ======================
   CLOSE BOOKING ON OUTSIDE CLICK
====================== */
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        showBooking &&
        bookingRef.current &&
        !bookingRef.current.contains(event.target)
      ) {
        setShowBooking(false);
        setSelectedCategory("");
        setSelectedArea("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showBooking]);

  const handleShare = async () => {
    const shareUrl = window.location.href;

    if (navigator.share) {
      try {
        await navigator.share({
          title: `Solicitor Profile – ${solicitor.name}`,
          text: "Check out this verified solicitor profile",
          url: shareUrl
        });
      } catch (err) {
        console.log("Share cancelled");
      }
    } else {
      await navigator.clipboard.writeText(shareUrl);
      alert("Profile link copied to clipboard");
    }
  };

  const getExpertBadge = (s) => {
    const level = s?.profile?.advocateLevel;
    const totalCases = Number(s?.profile?.totalCases || 0);

    if (level === "SENIOR" && totalCases >= 1) {
      return { type: "gold", label: "Expert" };
    }

    if (level === "MID" && totalCases >= 8) {
      return { type: "silver", label: "Pro" };
    }

    if ((level === "JUNIOR" || !level) && totalCases < 5) {
      return { type: "bronze", label: "Beginner" };
    }

    return null;
  };




  if (loading) return <div className="advocate-page-loading">Loading advocate profile...</div>;
  if (!solicitor) return <div className="advocate-not-found">Profile not found</div>;

  return (
    <>
      <Header />

      <main className="advocate-detail-container">
        {/* ================= HEADER SECTION ================= */}
        <section className="advocate-profile-header">
          <div className="advocate-profile-card">
            <div className="advocate-profile-top">
              <div className="advocate-profile-image-container">
                <img
                  src={solicitor.profile?.photo || "/avatar.png"}
                  alt={solicitor.name}
                  className="advocate-profile-photo"
                />
                {solicitor.verificationStatus === "VERIFIED" && (
                  <div className="advocate-verified-badge">
                    <FaCheckCircle className="advocate-verified-icon" />
                    <span>Verified Solicitor</span>
                  </div>
                )}
              </div>

              <div className="advocate-profile-info">
                <div className="advocate-name-row">
                  <h1 className="advocate-name">{solicitor.name}</h1>

                  {/* Advocate level */}
                  <div className="advocate-level-badge">
                    <FaGraduationCap />
                    <span>{solicitor.profile?.advocateLevel || "Senior"} Advocate</span>
                  </div>

                  {/* ✅ Expert badge (from User table) */}
                  {(() => {
                    const badge = getExpertBadge(solicitor);
                    if (!badge) return null;

                    return (
                      <div className={`advocate-skill-badge ${badge.type}`}>
                        {badge.type === "gold" && <FaCrown />}
                        {badge.type === "silver" && <FaMedal />}
                        {badge.type === "bronze" && <FaAward />}
                        <span>{badge.label}</span>
                      </div>
                    );
                  })()}
                </div>


                <p className="advocate-practice-name">
                  {solicitor.practice?.practiceName || "Independent Legal Practice"}
                </p>

                <div className="advocate-stats-grid">
                  <div className="advocate-stat-item">
                    <FaMapMarkerAlt className="advocate-stat-icon" />
                    <div>
                      <span className="advocate-stat-value">{solicitor.profile?.address?.city || "Location"}</span>
                      <span className="advocate-stat-label">Based In</span>
                    </div>
                  </div>
                  <div className="advocate-stat-item">
                    <FaBriefcase className="advocate-stat-icon" />
                    <div>
                      <span className="advocate-stat-value">{solicitor.profile?.experience || "0"}+ Years</span>
                      <span className="advocate-stat-label">Experience</span>
                    </div>
                  </div>
                  <div className="advocate-stat-item">
                    <FaBalanceScale className="advocate-stat-icon" />
                    <div>
                      <span className="advocate-stat-value">
                        {about?.specializations?.length || 0}
                      </span>
                      <span className="advocate-stat-label">Specializations</span>
                    </div>
                  </div>


                  {/* ✅ Consultation Fee */}
                  <div className="advocate-stat-item">
                    <FaClock className="advocate-stat-icon" />
                    <div>
                      <span className="advocate-stat-value">
                        {solicitor.profile?.consultationFee > 0
                          ? `£${solicitor.profile.consultationFee}`
                          : "Free"}
                      </span>
                      <span className="advocate-stat-label">Consultation Fee</span>
                    </div>
                  </div>


                </div>

                {/* ✅ ACTION BUTTONS */}
                <div className="advocate-action-buttons">
                  <button
                    className="advocate-btn-primary"
                    onClick={() => setShowBooking(!showBooking)}
                  >
                    <FaCalendarAlt className="advocate-btn-icon" />
                    <span>Book Appointment</span>
                    <FaArrowRight className="advocate-btn-arrow" />
                  </button>

                  {/* SHARE BUTTON */}
                  <button
                    className="advocate-btn-share"
                    onClick={handleShare}
                    title="Share profile"
                  >
                    <FaShareAlt />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ================= BOOKING SELECTOR ================= */}
        {showBooking && (
          <section className="advocate-booking-selector">
            <div className="advocate-booking-card" ref={bookingRef}>
              <div className="advocate-booking-header">
                <FaCalendarAlt className="advocate-booking-icon" />
                <h3 className="advocate-booking-title">Book Legal Consultation</h3>
              </div>

              <div className="advocate-booking-form">
                <div className="advocate-form-group">
                  <label className="advocate-form-label">
                    <FaBalanceScale className="advocate-label-icon" />
                    <span>Practice Category</span>
                  </label>
                  <select
                    className="advocate-form-select"
                    value={selectedCategory}
                    onChange={(e) => {
                      setSelectedCategory(e.target.value);
                      setSelectedArea("");
                    }}
                  >
                    <option value="">Select legal category</option>
                    {solicitor.practiceAreas.map((p) => (
                      <option key={p.category} value={p.category}>
                        {p.category}
                      </option>
                    ))}
                  </select>
                </div>

                {selectedCategory && (
                  <div className="advocate-form-group">
                    <label className="advocate-form-label">
                      <FaGavel className="advocate-label-icon" />
                      <span>Practice Area</span>
                    </label>
                    <select
                      className="advocate-form-select"
                      value={selectedArea}
                      onChange={(e) => setSelectedArea(e.target.value)}
                    >
                      <option value="">Select specific area</option>
                      {solicitor.practiceAreas
                        .find((p) => p.category === selectedCategory)
                        ?.areas.map((area) => (
                          <option key={area} value={area}>
                            {area}
                          </option>
                        ))}
                    </select>
                  </div>
                )}

                <button className="advocate-btn-primary advocate-booking-btn" onClick={proceedBooking}>
                  <span>Continue to Booking</span>
                  <FaArrowRight className="advocate-btn-arrow" />
                </button>
              </div>
            </div>
          </section>
        )}

        {/* ================= CONTENT GRID ================= */}
        <section className="advocate-content-grid">
          {/* LEFT COLUMN */}
          <div className="advocate-left-column">
            {/* About Section */}
            <div className="advocate-content-card">
              <div className="advocate-card-header">
                <FaUserTie className="advocate-card-icon" />
                <h3 className="advocate-card-title">Professional Background</h3>
              </div>
              <div className="advocate-about-content">
                {(about?.about || "No professional background available.")
                  .split("\n\n")
                  .filter(Boolean)
                  .map((p, i) => (
                    <p key={i} className="advocate-about-paragraph">{p}</p>
                  ))}
              </div>
            </div>

            {/* Practice Areas Section */}
            <div className="advocate-content-card">
              <div className="advocate-card-header">
                <FaBalanceScale className="advocate-card-icon" />
                <h3 className="advocate-card-title">Legal Expertise</h3>
              </div>
              <div className="advocate-expertise-grid">
                {solicitor.practiceAreas.map((item, i) => {
                  const pct = getPracticePercentage(item.areas);
                  return (
                    <div key={i} className="advocate-expertise-item">
                      <div className="advocate-expertise-header">
                        <h4 className="advocate-expertise-title">{item.category}</h4>
                        <span className="advocate-expertise-percentage">{pct}%</span>
                      </div>
                      <div className="advocate-expertise-bar">
                        <div
                          className={`advocate-expertise-fill ${getBarColorClass(pct)}`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <div className="advocate-areas-list">
                        {item.areas.map((area, idx) => (
                          <span key={idx} className="advocate-area-tag">{area}</span>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* RIGHT COLUMN */}
          <div className="advocate-right-column">
            {/* Courts & Specializations */}
            {(about?.courts?.length > 0 || about?.specializations?.length > 0) && (
              <div className="advocate-content-card">
                <div className="advocate-card-header">
                  <FaBuilding className="advocate-card-icon" />
                  <h3 className="advocate-card-title">Courts & Specializations</h3>
                </div>

                {about?.courts?.length > 0 && (
                  <div className="advocate-courts-section">
                    <h4 className="advocate-section-subtitle">Courts Practiced</h4>
                    <div className="advocate-tags-container">
                      {about.courts.map((court) => (
                        <span key={court} className="advocate-court-tag">
                          <FaGavel className="advocate-tag-icon" />
                          {court}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {about?.specializations?.length > 0 && (
                  <div className="advocate-specializations-section">
                    <h4 className="advocate-section-subtitle">Specializations</h4>
                    <div className="advocate-tags-container">
                      {about.specializations.map((spec) => (
                        <span key={spec} className="advocate-specialization-tag">
                          <FaCertificate className="advocate-tag-icon" />
                          {spec}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Legal Videos */}
            {/* Legal Videos */}
            {about?.legalVideoUrls?.length > 0 && (
              <div className="advocate-content-card">
                <div className="advocate-card-header">
                  <FaVideo className="advocate-card-icon" />
                  <h3 className="advocate-card-title">Legal Insights</h3>
                </div>

                <div className="advocate-video-grid">
                  {(showAllVideos
                    ? about.legalVideoUrls               // 🔥 show all
                    : about.legalVideoUrls.slice(0, 3)   // 🔥 show only first 3
                  ).map((url, i) => (
                    <div key={i} className="advocate-video-wrapper">
                      <iframe
                        src={getEmbedUrl(url)}
                        title={`Legal advice video ${i + 1}`}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        className="advocate-video-iframe"
                      />

                      <div className="advocate-video-overlay">
                        <FaVideo className="advocate-video-icon" />
                      </div>
                    </div>
                  ))}
                </div>

                {/* 🔥 SHOW MORE / LESS BUTTON */}
                {about.legalVideoUrls.length > 3 && (
                  <p
                    className="advocate-video-more"
                    onClick={() => setShowAllVideos(!showAllVideos)}
                    style={{ cursor: "pointer" }}
                  >
                    {showAllVideos
                      ? "Show less videos"
                      : `+ ${about.legalVideoUrls.length - 3} more videos available`}
                  </p>
                )}
              </div>
            )}

          </div>
        </section>

        {/* ================= REVIEWS SECTION ================= */}
        <section className="advocate-reviews-section">
          <div className="advocate-content-card advocate-reviews-card">
            <Reviews solicitorId={id} />
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}